/**
 * Hero Deluxe Block - JavaScript functionality
 * Handles carousel navigation and video play/pause controls
 */

const initHeroDeluxe = () => {
	const heroes = document.querySelectorAll('.block-hero-deluxe');

	heroes.forEach((hero) => {
		if (hero.dataset.initialized) return;

		const mediaType = hero.dataset.mediaType;

		if (mediaType === 'carousel') {
			initCarousel(hero);
		} else if (mediaType === 'video') {
			initVideo(hero);
		}

		hero.dataset.initialized = 'true';
	});
};

/**
 * Initialize carousel functionality
 */
const initCarousel = (hero) => {
	const carousel = hero.querySelector('.block-hero-deluxe__carousel');
	if (!carousel) return;

	const track = carousel.querySelector('.block-hero-deluxe__carousel-track');
	const slides = carousel.querySelectorAll('.block-hero-deluxe__slide');
	const dots = carousel.querySelectorAll('.block-hero-deluxe__carousel-dot');
	const contentPanels = hero.querySelectorAll('.block-hero-deluxe__content[data-slide-index]');
	const prevBtn = carousel.querySelector('.block-hero-deluxe__carousel-arrow--prev');
	const nextBtn = carousel.querySelector('.block-hero-deluxe__carousel-arrow--next');
	const autoAdvance = hero.dataset.autoAdvance === 'true';
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	let currentIndex = 0;
	let autoplayInterval = null;
	let isPaused = false;
	const slideCount = slides.length;
	const autoplayDelay = 5000;

	/**
	 * Go to a specific slide
	 */
	const goToSlide = (index, animate = true) => {
		// Wrap around
		if (index < 0) index = slideCount - 1;
		if (index >= slideCount) index = 0;

		// Slide the track
		if (track) {
			track.style.transform = `translateX(-${index * 100}%)`;
		}

		// Update slide aria attributes
		slides.forEach((slide, i) => {
			slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
		});

		// Update dots
		dots.forEach((dot, i) => {
			const isActive = i === index;
			dot.classList.toggle('block-hero-deluxe__carousel-dot--active', isActive);
			dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		// Update content panels
		contentPanels.forEach((panel) => {
			const panelIndex = parseInt(panel.dataset.slideIndex, 10);
			const isActive = panelIndex === index;
			panel.classList.toggle('block-hero-deluxe__content--active', isActive);
			panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

			panel.querySelectorAll('a, button').forEach((el) => {
				el.setAttribute('tabindex', isActive ? '0' : '-1');
			});
		});

		currentIndex = index;
	};

	/**
	 * Go to next slide
	 */
	const nextSlide = () => {
		goToSlide(currentIndex + 1);
	};

	/**
	 * Go to previous slide
	 */
	const prevSlide = () => {
		goToSlide(currentIndex - 1);
	};

	/**
	 * Start autoplay
	 */
	const startAutoplay = () => {
		if (!autoAdvance || prefersReducedMotion || isPaused) return;
		
		stopAutoplay();
		autoplayInterval = setInterval(nextSlide, autoplayDelay);
	};

	/**
	 * Stop autoplay
	 */
	const stopAutoplay = () => {
		if (autoplayInterval) {
			clearInterval(autoplayInterval);
			autoplayInterval = null;
		}
	};

	// Event listeners
	if (prevBtn) {
		prevBtn.addEventListener('click', () => {
			prevSlide();
			stopAutoplay();
		});
	}

	if (nextBtn) {
		nextBtn.addEventListener('click', () => {
			nextSlide();
			stopAutoplay();
		});
	}

	dots.forEach((dot) => {
		dot.addEventListener('click', () => {
			const slideIndex = parseInt(dot.dataset.slide, 10);
			goToSlide(slideIndex);
			stopAutoplay();
		});
	});

	// Keyboard navigation
	carousel.addEventListener('keydown', (e) => {
		if (e.key === 'ArrowLeft') {
			e.preventDefault();
			prevSlide();
			stopAutoplay();
		} else if (e.key === 'ArrowRight') {
			e.preventDefault();
			nextSlide();
			stopAutoplay();
		}
	});

	// Pause on hover/focus
	carousel.addEventListener('mouseenter', () => {
		isPaused = true;
		stopAutoplay();
	});

	carousel.addEventListener('mouseleave', () => {
		isPaused = false;
		startAutoplay();
	});

	carousel.addEventListener('focusin', () => {
		isPaused = true;
		stopAutoplay();
	});

	carousel.addEventListener('focusout', (e) => {
		if (!carousel.contains(e.relatedTarget)) {
			isPaused = false;
			startAutoplay();
		}
	});

	// Touch/swipe support
	let touchStartX = 0;
	let touchEndX = 0;
	const swipeThreshold = 50;

	carousel.addEventListener('touchstart', (e) => {
		touchStartX = e.changedTouches[0].screenX;
	}, { passive: true });

	carousel.addEventListener('touchend', (e) => {
		touchEndX = e.changedTouches[0].screenX;
		const swipeDistance = touchEndX - touchStartX;

		if (Math.abs(swipeDistance) > swipeThreshold) {
			if (swipeDistance > 0) {
				prevSlide();
			} else {
				nextSlide();
			}
			stopAutoplay();
		}
	}, { passive: true });

	// IntersectionObserver for viewport visibility
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					startAutoplay();
				} else {
					stopAutoplay();
				}
			});
		},
		{ threshold: 0.5 }
	);

	observer.observe(carousel);

	// Start autoplay if enabled
	startAutoplay();
};

/**
 * Initialize video functionality
 */
const initVideo = (hero) => {
	const videoControl = hero.querySelector('.block-hero-deluxe__video-control');
	const video = hero.querySelector('.block-hero-deluxe__video');
	const videoWrapper = hero.querySelector('.block-hero-deluxe__video-wrapper');
	const poster = hero.querySelector('.block-hero-deluxe__poster');

	let isPlaying = true;
	let player = null; // For YouTube/Vimeo API

	/**
	 * Update control button state
	 */
	const updateControlState = (playing) => {
		if (!videoControl) return;

		videoControl.dataset.state = playing ? 'playing' : 'paused';
		videoControl.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
	};

	/**
	 * Show poster image to mask YouTube/Vimeo overlays when paused
	 */
	const showPoster = () => {
		if (poster) {
			poster.classList.remove('block-hero-deluxe__poster--hidden');
		}
	};

	/**
	 * Hide poster image when video resumes playback
	 */
	const hidePoster = () => {
		if (poster) {
			poster.classList.add('block-hero-deluxe__poster--hidden');
		}
	};

	// Hide poster when local video starts playing
	if (video && video.tagName === 'VIDEO') {
		video.addEventListener('playing', hidePoster, { once: true });
	}

	/**
	 * Toggle video playback
	 */
	const togglePlayback = () => {
		if (video && video.tagName === 'VIDEO') {
			if (video.paused) {
				video.play();
				isPlaying = true;
				hidePoster();
			} else {
				video.pause();
				isPlaying = false;
				showPoster();
			}
			updateControlState(isPlaying);
		} else if (player) {
			if (isPlaying) {
				if (player.contentWindow) {
					// YouTube
					player.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
					// Vimeo
					player.contentWindow.postMessage('{"method":"pause"}', '*');
				}
				isPlaying = false;
				showPoster();
			} else {
				if (player.contentWindow) {
					// YouTube
					player.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
					// Vimeo
					player.contentWindow.postMessage('{"method":"play"}', '*');
				}
				isPlaying = true;
				hidePoster();
			}
			updateControlState(isPlaying);
		}
	};

	// Lazy load iframe videos
	const lazyLoadIframe = () => {
		const iframe = hero.querySelector('iframe[data-src]');
		if (iframe && !iframe.src) {
			iframe.src = iframe.dataset.src;
			player = iframe;
			
			// Hide poster when iframe is ready
			if (poster) {
				iframe.addEventListener('load', () => {
					poster.classList.add('block-hero-deluxe__poster--hidden');
				});
			}
		}
	};

	// IntersectionObserver for lazy loading and auto-pause
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					lazyLoadIframe();
					
					// Resume video if it was playing
					if (video && video.tagName === 'VIDEO') {
						video.play().catch(() => {});
					}
				} else {
					// Pause when out of view
					if (video && video.tagName === 'VIDEO') {
						video.pause();
					}
				}
			});
		},
		{ threshold: 0.25 }
	);

	observer.observe(hero);

	// Play/pause button click handler
	if (videoControl) {
		videoControl.addEventListener('click', togglePlayback);
	}

	// Keyboard support for video control
	if (videoControl) {
		videoControl.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				togglePlayback();
			}
		});
	}
};

// DOM ready
document.addEventListener('DOMContentLoaded', initHeroDeluxe);

// Gutenberg block preview support
if (window.acf) {
	window.acf.addAction('render_block_preview/type=hero-deluxe', initHeroDeluxe);
}

// Re-initialize on page visibility change (for autoplay resume)
document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'visible') {
		// Reinitialize to restart autoplay where appropriate
		document.querySelectorAll('.block-hero-deluxe[data-initialized="true"]').forEach((hero) => {
			hero.dataset.initialized = '';
		});
		initHeroDeluxe();
	}
});
